import {useState, useEffect, useContext, useMemo, useCallback} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faExclamation, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";

// Utils
import useMountedState from "../../hooks/useMountedState.js";
import {getAncestors, COMPONENT} from "../../utils/builder.js";
import {exists} from "../../utils/helpers.js";
import {
  getAncestryName,
  getWeatherDependsLabel,
  getWeatherUnits,
  isWeatherConditionDepends
} from "./helpers.js";

// Components
import Modal from "../../components/Modal.js";
import ModalNotificationsUpdate from "./ModalNotificationsUpdate.js";

// Style
import {border, colors, pad, radius} from "../../style/components/variables.js";
import {
  Heading,
  Text,
  List,
  ListItem,
  Abbr,
  Button,
  Label,
  Inline,
  Error
} from "../../style/components/general.js";

const checkMap = {
  "===": "is",
  "!==": "is not",
  true: "True",
  false: "False",
  ">": "is greater than",
  "<": "is less than",
  ">=": "is greater than or equal to",
  "<=": "is less than or equal to",
  contains: "has"
};

const compareMap = {
  0: "0",
  1: "1",
  checked: "all checked",
  unchecked: "all unchecked",
  one: "one checked",
  more: "at least one checked",
  some: "at least one unchecked"
};

const ModalNotifications = ({
  visible,
  setVisible,
  states,
  notifications,
  setNotifications,
  handleSave,
  state
}) => {
  const {allIds: nAllIds, byId: nById} = notifications;

  const useableStates = useMemo(() => {
    const keys = Object.keys(states);

    if (keys.length === 0) return {};
    return Object.fromEntries(
      keys.filter(key => states[key]?.builder?.allIds?.length > 0).map(key => [key, states[key]])
    );
  }, [states]);

  const defaultBuilder = useMemo(() => states["0"]?.builder || {byId: {}, allIds: []}, [states]);

  const stateBuilder = useMemo(
    () => (state ? states[state.id]?.builder : undefined),
    [state, states]
  );

  const getField = useCallback(
    name => {
      if (defaultBuilder?.byId?.[name] && !defaultBuilder.byId[name].deleted)
        return defaultBuilder.byId[name];
      if (stateBuilder?.byId?.[name] && !stateBuilder.byId[name].deleted)
        return stateBuilder.byId[name];
      return null;
    },
    [defaultBuilder, stateBuilder]
  );

  const getBuilder = useCallback(
    name => {
      if (defaultBuilder?.byId?.[name] && !defaultBuilder.byId[name].deleted) return defaultBuilder;
      if (stateBuilder?.byId?.[name] && !stateBuilder.byId[name].deleted)
        return {allIds: stateBuilder.allIds, byId: {...defaultBuilder.byId, ...stateBuilder.byId}};
      return null;
    },
    [defaultBuilder, stateBuilder]
  );

  const isFromDefault = useCallback(
    name => {
      if (defaultBuilder?.byId?.[name] && !defaultBuilder.byId[name].deleted) return true;
      if (stateBuilder?.byId?.[name] && !stateBuilder.byId[name].deleted) return false;
      return false;
    },
    [defaultBuilder, stateBuilder]
  );

  const isMounted = useMountedState();
  const {roles} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);

  const [openUpdate, setOpenUpdate] = useState(false);
  const [target, setTarget] = useState(null);

  const hasRoleRecipientsEnabled = useMemo(() => {
    if (roles?.length > 0 && settings?.roleRecipients === "true") return true;
    return false;
  }, [roles, settings]);

  const current = useMemo(() => {
    if (target && nById && nById[target]) {
      const {trigger, depends, template, recipients, conditionArray, operator, type, name, prompt} =
        nById[target];

      const notification = {
        trigger,
        type,
        name,
        prompt,
        ...template
      };

      const currentRecipientsAreRoles = type ? type === "roles" : hasRoleRecipientsEnabled;
      if (currentRecipientsAreRoles === hasRoleRecipientsEnabled)
        notification.recipients = recipients;
      else notification.recipients = [];

      if (trigger === "Unacceptable Parameter" || depends)
        notification.condition = {
          depends,
          conditionArray,
          operator
        };

      return notification;
    }

    return null;
  }, [target, nById, hasRoleRecipientsEnabled]);

  useEffect(() => {
    if (isMounted() && target) setOpenUpdate(true);
  }, [isMounted, target]);

  const getAlertCondition = (alertCondition, depends) => {
    const dependsObject = getField(depends);
    if (dependsObject?.element === COMPONENT) {
      if (alertCondition === "enabled") return dependsObject.enabledLabel || "Enabled";

      if (alertCondition === "disabled") return dependsObject.disabledLabel || "Disabled";

      return alertCondition;
    }
    return alertCondition;
  };

  const handleDelete = id => {
    // Important: clone `allIds` and `byId`.
    // The previous code mutated nested structures in-place, which can keep the same
    // array/object references and prevent `useMemo` from re-running, causing
    // `allIds` to appear out of sync with `byId`.
    const updated = {
      ...notifications,
      allIds: [...(notifications?.allIds ?? [])],
      byId: {...(notifications?.byId ?? {})}
    };

    if (updated.byId[id]) delete updated.byId[id];

    updated.allIds = updated.allIds.filter(notificationId => notificationId !== id);

    setNotifications(updated);
    handleSave(updated);
  };

  const renderList = list =>
    list.map(notificationId => {
      if (!nById[notificationId]) return null;

      const {depends, conditionArray, operator, template, recipients, type, trigger, name} =
        nById[notificationId];

      const recipientsAreRoles = type === "roles";
      let settingAlert = "";

      if (!recipientsAreRoles && hasRoleRecipientsEnabled)
        settingAlert = (
          <StyledAbbr title="Needs to be reconfigured for role recipients">
            <FontAwesomeIcon icon={faExclamation} />
          </StyledAbbr>
        );

      if (recipientsAreRoles && !hasRoleRecipientsEnabled)
        settingAlert = (
          <StyledAbbr title="Needs to be reconfigured for user recipients">
            <FontAwesomeIcon icon={faExclamation} />
          </StyledAbbr>
        );

      const title = template?.subject?.toUpperCase() || name?.toUpperCase();

      const dependsBuilder = depends ? getBuilder(depends) : null;
      const isDefault = depends ? isFromDefault(depends) : null;

      return (
        <Notification key={notificationId}>
          <ListItem key={notificationId}>
            {title && (
              <StyledText bold>
                {title}
                {settingAlert}
              </StyledText>
            )}
            {depends && (
              <Text>
                When
                {isWeatherConditionDepends(depends)
                  ? ` ${getWeatherDependsLabel(depends)} is`
                  : ""}
                :&nbsp;
                {conditionArray.map(({check, compare}, i) => {
                  const weather = isWeatherConditionDepends(depends);
                  const units = weather ? getWeatherUnits(depends) : "";
                  const dependsObject = weather ? null : getField(depends);
                  const compareValue =
                    compare in compareMap ? compareMap[compare] : compare?.replace("Other: ", "");
                  return (
                    <span key={`${check}-${compare}`}>
                      {i > 0 && `${operator} `}
                      {!weather && dependsObject && (
                        <>
                          {dependsObject.parentName && dependsBuilder
                            ? `${getAncestors(
                                dependsObject.parentName,
                                dependsBuilder.byId
                              )}${"\u00A0"}`
                            : ""}
                          {dependsObject.label}&nbsp;
                          {!isDefault && `(${state?.name || "State"})${"\u00A0"}`}
                        </>
                      )}
                      {checkMap[check] || check}&nbsp;
                      {compareValue}
                      {units ? ` ${units}` : ""}
                      &nbsp;
                    </span>
                  );
                })}
              </Text>
            )}
            {trigger === "Unacceptable Parameter" && (
              <ParamWrapper>
                {conditionArray
                  ?.filter(
                    ({depends: condDepends}) =>
                      getField(condDepends) || isWeatherConditionDepends(condDepends)
                  )
                  ?.map(
                    ({depends: condDepends, aMin, aMax, alertCondition, check, compare}, i) => {
                      const weather = isWeatherConditionDepends(condDepends);
                      const condDependsObject = weather ? null : getField(condDepends);
                      const condDependsBuilder = weather ? null : getBuilder(condDepends);
                      const units = weather
                        ? getWeatherUnits(condDepends)
                        : condDependsObject?.units;
                      let compareValue = compare;
                      if (compare && typeof compare === "string" && compare.includes("Other: "))
                        compareValue = compare.replace("Other: ", "");
                      if (compare in compareMap) compareValue = compareMap[compare];

                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={`${condDepends}-${i}`}>
                          <Text bold>
                            {weather
                              ? getWeatherDependsLabel(condDepends)
                              : getAncestryName(condDependsObject, condDependsBuilder)}
                          </Text>
                          {weather && check && compare !== undefined && compare !== null && (
                            <Text>
                              {checkMap[check] || check}&nbsp;{compareValue}
                              {units ? ` ${units}` : ""}
                            </Text>
                          )}
                          {!weather && exists(aMin) && exists(aMax) && (
                            <Text>
                              Acceptable Range: {aMin} - {aMax} {units}
                            </Text>
                          )}
                          {!weather && exists(aMin) && !exists(aMax) && (
                            <Text>
                              Minimum Value: {aMin} {units}
                            </Text>
                          )}
                          {!weather && !exists(aMin) && exists(aMax) && (
                            <Text>
                              Maximum Value: {aMax} {units}
                            </Text>
                          )}
                          {!weather && Array.isArray(alertCondition) && (
                            <Text>Unacceptable Values: {alertCondition.join(", ")}</Text>
                          )}
                          {!weather && typeof alertCondition === "string" && (
                            <Text>
                              Unacceptable Value: {getAlertCondition(alertCondition, condDepends)}
                            </Text>
                          )}
                        </div>
                      );
                    }
                  )}
              </ParamWrapper>
            )}
            {recipients?.length ? (
              <Text>
                Notify:&nbsp;
                {recipients.map((recipient, index) => (
                  <span key={recipient}>
                    {recipient}
                    {index !== recipients.length - 1 && ", "}
                  </span>
                ))}
              </Text>
            ) : (
              <Text>Sent to facility notification recipients</Text>
            )}
          </ListItem>
          <Options>
            <IconButton type="button" onClick={() => setTarget(notificationId)}>
              <Abbr title="Edit">
                <FontAwesomeIcon icon={faEdit} />
              </Abbr>
            </IconButton>
            <IconButton type="button" onClick={() => handleDelete(notificationId)} delete>
              <Abbr title="Delete">
                <FontAwesomeIcon icon={faTrash} />
              </Abbr>
            </IconButton>
          </Options>
        </Notification>
      );
    });

  const renderNotifications = list => {
    if (list.length === 0) return null;
    return renderList(list);
  };

  const completed = useMemo(
    () =>
      nAllIds?.filter(
        id =>
          (!nById[id].depends || nById[id].depends === null) &&
          nById[id].trigger !== "Unacceptable Parameter"
      ) || [],
    [nAllIds, nById]
  );

  const field = useMemo(
    () => nAllIds?.filter(id => nById[id].depends && nById[id].depends !== null) || [],
    [nAllIds, nById]
  );

  const unacceptable = useMemo(
    () => nAllIds?.filter(id => nById[id].trigger === "Unacceptable Parameter") || [] ,
    [nAllIds, nById]
  );

  if (openUpdate)
    return (
      <ModalNotificationsUpdate
        visible={visible}
        setVisible={setVisible}
        goBack={setOpenUpdate}
        states={states}
        edit={target}
        current={current}
        setEdit={setTarget}
        notifications={notifications}
        handleSave={handleSave}
        state={state}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <Heading>Checksheet Notifications</Heading>

      {nAllIds && nAllIds.length > 0 ? (
        <>
          {completed?.length ? (
            <div>
              <Label bold>Completed Notifications</Label>
              <br />
              <List column>{renderNotifications(completed)}</List>
            </div>
          ) : (
            ""
          )}

          {field?.length ? (
            <div>
              <Label bold>Field Notifications</Label>
              <br />
              <List column>{renderNotifications(field)}</List>
            </div>
          ) : (
            ""
          )}

          {unacceptable?.length ? (
            <div>
              <Label bold>Unacceptable Parameters</Label>
              <br />
              <List column>{renderNotifications(unacceptable)}</List>
            </div>
          ) : (
            ""
          )}
        </>
      ) : (
        <Text>Checksheet specific notifications have not been configured.</Text>
      )}

      {Object.keys(useableStates).length === 0 ? (
        <StyledError>
          Checksheet must have elements added in order to create a restriction.
        </StyledError>
      ) : (
        <Button
          type="button"
          onClick={() => {
            setOpenUpdate(true);
          }}>
          <FontAwesomeIcon icon={faPlus} />
          &nbsp;New
        </Button>
      )}
    </Modal>
  );
};

ModalNotifications.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  states: PropTypes.objectOf(PropTypes.any).isRequired,
  notifications: PropTypes.objectOf(PropTypes.any).isRequired,
  setNotifications: PropTypes.func.isRequired,
  state: PropTypes.objectOf(PropTypes.any),
  handleSave: PropTypes.func.isRequired
};

// Style Overrides
const Notification = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
  margin: ${pad}px 0;
`;

const Options = styled(Inline)`
  align-items: start;
`;

const IconButton = styled(Button)`
  width: auto;

  ${({delete: d, theme}) =>
    d &&
    css`
      background: ${theme.error};
    `}
`;

const ParamWrapper = styled.div`
  margin-top: ${pad}px;
`;

const StyledAbbr = styled(Abbr)`
  background-color: ${colors.red};
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-left: ${pad}px;
  padding-left: 1px;

  svg {
    fill: ${({theme}) => theme.tertiary};
    height: 16px;
    width: 16px;
  }
`;

const StyledText = styled(Text)`
  display: flex;
`;

const StyledError = styled(Error)`
  margin-bottom: ${pad}px;
`;

export default ModalNotifications;
